import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faLine,
  faLinkedinIn,
  faPinterest,
  faRedditAlien,
  faTelegram,
  faWhatsapp,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import { addYears } from 'date-fns';
import { addShareSearchParams, type SocialShareSearchParams } from './helpers/add-tracking-params';

/*
 * ! ! ! IMPORTANT ! ! !
 *
 * This file duplicates existing functionality in an attempt to
 * simplify existing social sharing code, whilst gradually
 * transitioning to the new implementation.
 *
 * Old implementation can be found in `../../page-share/page-share.constants.ts`.
 */

/**
 * **Source of truth** for supported social media platforms.
 *
 * Other constants are derived from this, so simply define new
 * entry here and it will be added to:
 *
 * `SocialPlatformId`
 * - enum-like-const with uppercased `id` as key
 *
 * `SocialPlatforms`
 * - a `Map` of platform metadata by `SocialPlatformId`
 */
const SOCIAL_PLATFORMS = [
  {
    id: 'facebook',
    displayName: 'Facebook',
    icon: faFacebook,
    brandColor: '#1877f2',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://www.facebook.com/sharer/sharer.php');
      shareLink.searchParams.set('u', urlToBeShared);
      shareLink.searchParams.set('t', description);

      return shareLink.toString();
    },
  },
  {
    id: 'linkedin',
    displayName: 'LinkedIn',
    icon: faLinkedinIn,
    brandColor: '#0077b5',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://www.linkedin.com/feed');
      shareLink.searchParams.set('shareActive', 'true');
      shareLink.searchParams.set('shareUrl', urlToBeShared);
      shareLink.searchParams.set('text', description);

      return shareLink.toString();
    },
  },
  {
    id: 'twitter',
    displayName: 'Twitter / X',
    icon: faXTwitter,
    brandColor: '#121212',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://x.com/intent/post');
      shareLink.searchParams.set('url', urlToBeShared);
      shareLink.searchParams.set('text', description);
      shareLink.searchParams.set('via', THM_SOCIAL_HANDLES[SocialPlatformId.TWITTER]);
      shareLink.searchParams.set('hashtags', 'tryhackme');

      return shareLink.toString();
    },
  },
  {
    id: 'whatsapp',
    displayName: 'WhatsApp',
    icon: faWhatsapp,
    brandColor: '#25d366',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://wa.me/');
      shareLink.searchParams.set('text', `${description}\n${urlToBeShared}`);

      return shareLink.toString();
    },
  },
  {
    id: 'reddit',
    displayName: 'Reddit',
    icon: faRedditAlien,
    brandColor: '#ff5700',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://www.reddit.com/submit');
      shareLink.searchParams.set('url', urlToBeShared);
      shareLink.searchParams.set('title', description);

      return shareLink.toString();
    },
  },
  {
    id: 'telegram',
    displayName: 'Telegram',
    icon: faTelegram,
    brandColor: '#0088cc',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://t.me/share/url');
      shareLink.searchParams.set('url', urlToBeShared);
      shareLink.searchParams.set('text', description);

      return shareLink.toString();
    },
  },
  {
    id: 'line',
    displayName: 'Line',
    icon: faLine,
    brandColor: '#00B900',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://social-plugins.line.me/lineit/share');
      shareLink.searchParams.set('url', urlToBeShared);
      shareLink.searchParams.set('text', description);

      return shareLink.toString();
    },
  },
  {
    id: 'pinterest',
    displayName: 'Pinterest',
    icon: faPinterest,
    brandColor: '#E60023',
    shareLink: ({ url, description }: SocialPost, searchParams: SocialShareSearchParams) => {
      const urlToBeShared = addShareSearchParams(url, searchParams);

      const shareLink = new URL('https://pinterest.com/pin/create/button/');
      shareLink.searchParams.set('url', urlToBeShared);
      shareLink.searchParams.set('description', description);

      return shareLink.toString();
    },
  },
] as const;

type PlatformIdValues = (typeof SOCIAL_PLATFORMS)[number]['id'];

export type SocialPost = {
  url: string;
  description: string;
};

export interface SocialPlatform {
  readonly id: SocialPlatformId; // Should be in snake_case; avoid hyphens as this is used to derive keys in `SocialPlatformId` "enum"
  readonly displayName: string;
  readonly icon: IconDefinition;
  readonly brandColor: string;
  /** Link to share content on given social platform. */
  readonly shareLink: (socialPostInfo: SocialPost, searchParams: SocialShareSearchParams) => string;
}

/**
 * Dynamically computed and typed enum-like object.
 *
 * New entries in `SOCIAL_PLATFORMS` get automatically added. Example:
 * ```typescript
 * const SOCIAL_PLATFORMS = [
 *   ...,
 *   { id: 'new_platform', ... }
 * ]
 *
 * SocialPlatform.NEW_PLATFORM // "new_platform"
 * ```
 */
export const SocialPlatformId = Object.fromEntries(SOCIAL_PLATFORMS.map(({ id }) => [id.toUpperCase(), id])) as {
  [Id in PlatformIdValues as Uppercase<Id>]: Id;
};
export type SocialPlatformId = (typeof SocialPlatformId)[keyof typeof SocialPlatformId];

/** `Map` of platform by `id` */
export const SocialPlatforms: ReadonlyMap<SocialPlatformId, SocialPlatform> = new Map(
  SOCIAL_PLATFORMS.map((platform) => [platform.id, platform]),
);

export type CertificateDetails = {
  pathTitle: string;
  certificateId: string;
  dateIssued: Date;
  imageUrl: string;
  pdfUrl: string;
};

/** Used to add certificate to profile instead of posting */
export function linkedInCertificateLink(certificate: CertificateDetails): string {
  const { dateIssued, certificateId, pathTitle } = certificate;

  const certificateURL = `https://tryhackme.com/certificate/${certificateId}`;

  const year = dateIssued.getFullYear();
  const month = dateIssued.getMonth() + 1;
  const expiryDate = addYears(dateIssued, 3);
  const expiryYear = expiryDate.getFullYear();
  const expiryMonth = expiryDate.getMonth() + 1;

  const url = new URL('https://www.linkedin.com/profile/add');
  url.searchParams.set('startTask', 'CERTIFICATION_NAME');
  url.searchParams.set('name', `${pathTitle} Certificate`);
  url.searchParams.set('organizationId', '14055650');
  url.searchParams.set('issueYear', year.toString());
  url.searchParams.set('issueMonth', month.toString());
  url.searchParams.set('certUrl', certificateURL);
  url.searchParams.set('certId', certificateId);
  url.searchParams.set('expirationYear', expiryYear.toString());
  url.searchParams.set('expirationMonth', expiryMonth.toString());

  return url.toString();
}

export const THM_SOCIAL_HANDLES = Object.freeze({
  [SocialPlatformId.TWITTER]: 'tryhackme',
});

// Type validation to ensure SOCIAL_PLATFORMS matches `SocialPlatform` interface.
// Cannot be directly typed as `SocialPlatform.id` is derived from the constant.
const _typeCheck: readonly SocialPlatform[] = SOCIAL_PLATFORMS;
